import type {
  SimpleDateDTO,
  VacationDTO,
  VacationDataDTO,
  WorkdayDTO,
} from "@wkt/api/checkin/checkinDTO";
import { parseSimpleDateValue } from "@wkt/composables/useSimpleDate";
import { useCheckinStore } from "@wkt/stores/checkin";
import { useSessionCatalogStore } from "@wkt/stores/sessionCatalog";
import {
  isDateWithinVacation,
  resolveDayChips,
  type DayChip,
} from "@wkt/utils/resolveDayChips";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const COMPLETE_DAY_RECORD_TYPE = 7;
const HOURS_BAG_VACATION_ID = 6;
export const HOURS_BAG_UNIT_DAYS = 0;
export const HOURS_BAG_UNIT_HOURS = 1;

export function getBagUnit(
  company:
    | {
        defaultYearlyExtraUnit?: number | null;
        defaultYearlyHoursBag1Unit?: number | null;
        defaultYearlyHoursBag2Unit?: number | null;
        defaultYearlyHoursBag3Unit?: number | null;
        defaultYearlyHoursBag4Unit?: number | null;
        defaultYearlyHoursBag5Unit?: number | null;
      }
    | null
    | undefined,
  hoursBagType?: number | null,
): number {
  if (hoursBagType == null || hoursBagType === HOURS_BAG_VACATION_ID) {
    return HOURS_BAG_UNIT_DAYS;
  }

  if (!company) {
    return HOURS_BAG_UNIT_DAYS;
  }

  switch (hoursBagType) {
    case 7:
      return company.defaultYearlyExtraUnit ?? HOURS_BAG_UNIT_DAYS;
    case 1:
      return company.defaultYearlyHoursBag1Unit ?? HOURS_BAG_UNIT_DAYS;
    case 2:
      return company.defaultYearlyHoursBag2Unit ?? HOURS_BAG_UNIT_DAYS;
    case 3:
      return company.defaultYearlyHoursBag3Unit ?? HOURS_BAG_UNIT_DAYS;
    case 4:
      return company.defaultYearlyHoursBag4Unit ?? HOURS_BAG_UNIT_DAYS;
    case 5:
      return company.defaultYearlyHoursBag5Unit ?? HOURS_BAG_UNIT_DAYS;
    default:
      return HOURS_BAG_UNIT_DAYS;
  }
}

export function isHoursBagUnit(
  company:
    | {
        defaultYearlyExtraUnit?: number | null;
        defaultYearlyHoursBag1Unit?: number | null;
        defaultYearlyHoursBag2Unit?: number | null;
        defaultYearlyHoursBag3Unit?: number | null;
        defaultYearlyHoursBag4Unit?: number | null;
        defaultYearlyHoursBag5Unit?: number | null;
      }
    | null
    | undefined,
  hoursBagType?: number | null,
): boolean {
  return getBagUnit(company, hoursBagType) === HOURS_BAG_UNIT_HOURS;
}

interface VacationDateLike {
  year?: number | null;
  month?: number | null;
  day?: number | null;
}

interface VacationRecordLike {
  recordDateTs?: number | null;
  fromDate?: VacationDateLike | null;
  toDate?: VacationDateLike | null;
}

const parseCalendarDay = parseSimpleDateValue;

function toSortableDateValue(date?: VacationDateLike | null): number {
  if (!date?.year || !date?.month || !date?.day) {
    return Number.NEGATIVE_INFINITY;
  }

  return new Date(date.year, date.month - 1, date.day).getTime();
}

function getVacationRecordRecencyValue(record?: VacationRecordLike | null): number {
  if (!record) {
    return Number.NEGATIVE_INFINITY;
  }

  return Math.max(
    typeof record.recordDateTs === "number"
      ? record.recordDateTs
      : Number.NEGATIVE_INFINITY,
    toSortableDateValue(record.toDate),
    toSortableDateValue(record.fromDate),
  );
}

export function getMostRecentVacationRecord<T extends VacationRecordLike>(
  records: T[],
): T | null {
  let latestRecord: T | null = null;
  let latestValue = Number.NEGATIVE_INFINITY;

  for (const record of records) {
    const currentValue = getVacationRecordRecencyValue(record);
    if (currentValue > latestValue) {
      latestRecord = record;
      latestValue = currentValue;
    }
  }

  return latestRecord;
}

export interface VacationWorkdayLike {
  closed?: boolean | null;
  mustConfirm?: boolean | null;
  currentActivityStart?: string | null;
  totalActivityWorkHours?: number | null;
  totalActivityNonWorkHours?: number | null;
  activityList?: unknown[] | null;
  workdayDate?: SimpleDateDTO | null;
}

export function hasCompleteDayLeaveOnDate(
  date: SimpleDateDTO | null | undefined,
  vacations: VacationDTO[] | null | undefined,
  recordType = COMPLETE_DAY_RECORD_TYPE,
): boolean {
  if (!date) {
    return false;
  }

  return (
    vacations?.some(
      (vacation) =>
        vacation?.recordType === recordType &&
        isDateWithinVacation(date, vacation),
    ) ?? false
  );
}

export function workdayHasRecordedActivity(
  workday: VacationWorkdayLike | null | undefined,
): boolean {
  if (!workday) {
    return false;
  }

  if (workday.currentActivityStart != null) {
    return true;
  }

  if ((workday.totalActivityWorkHours ?? 0) > 0) {
    return true;
  }

  if ((workday.totalActivityNonWorkHours ?? 0) > 0) {
    return true;
  }

  return Array.isArray(workday.activityList) && workday.activityList.length > 0;
}

function workdayRequiresVacationConflictHandling(
  workday: VacationWorkdayLike | null | undefined,
): boolean {
  return workdayHasRecordedActivity(workday) || workday?.mustConfirm === true;
}

/** Open workday on a full-day leave date still pending confirm or with fichajes. */
export function isVacationWorkdayConflict(
  workday: VacationWorkdayLike | null | undefined,
  vacations: VacationDTO[] | null | undefined,
): boolean {
  if (!workday || workday.closed === true) {
    return false;
  }

  if (!hasCompleteDayLeaveOnDate(workday.workdayDate, vacations)) {
    return false;
  }

  return workdayRequiresVacationConflictHandling(workday);
}

/**
 * Drop empty leave-day shells that are not pending confirm (Monday vacation
 * after confirming Friday). Keep mustConfirm leftovers so the conflict warning
 * can render.
 */
export function suppressEmptyLeaveDayWorkday<T extends VacationWorkdayLike>(
  workday: T | null | undefined,
  vacations: VacationDTO[] | null | undefined,
): T | null {
  if (!workday || workday.closed === true) {
    return workday ?? null;
  }

  if (!hasCompleteDayLeaveOnDate(workday.workdayDate, vacations)) {
    return workday;
  }

  return workdayRequiresVacationConflictHandling(workday) ? workday : null;
}

export function normalizeWorkdayAfterVacationRefresh(
  workday: WorkdayDTO | null | undefined,
  vacationData: VacationDataDTO | null | undefined,
): WorkdayDTO | null {
  return suppressEmptyLeaveDayWorkday(
    workday ?? null,
    vacationData?.vacationData,
  );
}

export function useVacationHelpers() {
  const { t } = useI18n();
  const checkinStore = useCheckinStore();
  const sessionCatalogStore = useSessionCatalogStore();

  const currentDay = computed<SimpleDateDTO | null>(() => {
    if (checkinStore.currentWorkday?.workdayDate) {
      return checkinStore.currentWorkday.workdayDate;
    }

    return parseCalendarDay(checkinStore.todayCalendar?.calendarDay);
  });

  function getVacationByType(
    date = currentDay.value,
    recordType = COMPLETE_DAY_RECORD_TYPE,
  ): VacationDTO | null {
    return (
      checkinStore.vacationData?.vacationData?.find(
        (vacation) =>
          vacation?.recordType === recordType &&
          isDateWithinVacation(date, vacation),
      ) ?? null
    );
  }

  function getVacationByTypeBag(
    date = currentDay.value,
    recordType = COMPLETE_DAY_RECORD_TYPE,
    hoursBagType?: number | null,
  ): VacationDTO | null {
    return (
      checkinStore.vacationData?.vacationData?.find(
        (vacation) =>
          vacation?.recordType === recordType &&
          vacation?.hoursBagType === hoursBagType &&
          isDateWithinVacation(date, vacation),
      ) ?? null
    );
  }

  function hasLeave(
    date = currentDay.value,
    recordType = COMPLETE_DAY_RECORD_TYPE,
  ): boolean {
    return getVacationByType(date, recordType) != null;
  }

  function dayChips(date = currentDay.value): DayChip[] {
    const currentCalendar =
      checkinStore.getCalendarForDate(date) ?? checkinStore.todayCalendar;

    return resolveDayChips(
      date,
      currentCalendar,
      checkinStore.vacationData?.vacationData ?? [],
      sessionCatalogStore.company,
      t,
    );
  }

  const currentDayChips = computed(() => dayChips());

  return {
    currentDay,
    currentDayChips,
    hasLeave,
    getVacationByType,
    getVacationByTypeBag,
    dayChips,
  };
}
